import { supabase } from "@/lib/supabase";

export interface Conversation {
  id: string;
  product_id: number;
  buyer_id: string;
  seller_id: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_sender_id: string | null;
  product_title: string;
  product_image: string | null;
  other_user_id: string;
  other_name: string;
  other_avatar: string | null;
  unread_count: number;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  read_at: string | null;
}

interface RawConversation {
  id: string;
  product_id: number;
  buyer_id: string;
  seller_id: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_sender_id: string | null;
}

/** Inicia (o recupera) la conversación entre el comprador y el vendedor de una publicación. */
export async function startConversation(productId: number): Promise<string> {
  const { data, error } = await supabase.rpc("start_conversation", {
    p_product_id: productId,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

/** Lista las conversaciones del usuario, con producto y datos del otro participante. */
export async function fetchConversations(
  userId: string,
): Promise<Conversation[]> {
  const { data: convos, error } = await supabase
    .from("conversations")
    .select(
      "id, product_id, buyer_id, seller_id, created_at, updated_at, last_message_at, last_message_preview, last_sender_id",
    )
    .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
    .order("updated_at", { ascending: false });

  if (error) throw error;

  const list = (convos ?? []) as RawConversation[];
  if (list.length === 0) return [];

  const productIds = [...new Set(list.map((c) => c.product_id))];
  const otherIds = [
    ...new Set(
      list
        .flatMap((c) => [c.buyer_id, c.seller_id])
        .filter((id) => id !== userId),
    ),
  ];

  const [productsRes, profilesRes, unreadRes] = await Promise.all([
    supabase.from("products").select("id, title, images").in("id", productIds),
    supabase
      .from("profiles")
      .select("id, full_name, email, avatar_url")
      .in("id", otherIds),
    supabase
      .from("messages")
      .select("conversation_id")
      .is("read_at", null)
      .neq("sender_id", userId)
      .in("conversation_id", list.map((c) => c.id)),
  ]);

  if (productsRes.error) throw productsRes.error;
  if (profilesRes.error) throw profilesRes.error;
  if (unreadRes.error) throw unreadRes.error;

  const productsById = new Map(
    (productsRes.data ?? []).map((p) => [p.id, p]),
  );
  const profilesById = new Map(
    (profilesRes.data ?? []).map((p) => [p.id, p]),
  );
  const unreadByConv = new Map<string, number>();
  for (const m of unreadRes.data ?? []) {
    unreadByConv.set(m.conversation_id, (unreadByConv.get(m.conversation_id) ?? 0) + 1);
  }

  return list.map((c) => {
    const otherId = c.buyer_id === userId ? c.seller_id : c.buyer_id;
    const other = profilesById.get(otherId);
    const product = productsById.get(c.product_id);
    const images = Array.isArray(product?.images) ? product.images : [];
    return {
      ...c,
      product_title: product?.title ?? "Publicación",
      product_image: images[0] ?? null,
      other_user_id: otherId,
      other_name: other?.full_name ?? other?.email ?? "Usuario",
      other_avatar: other?.avatar_url ?? null,
      unread_count: unreadByConv.get(c.id) ?? 0,
    };
  });
}

export async function fetchMessages(
  conversationId: string,
): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from("messages")
    .select("id, conversation_id, sender_id, body, created_at, read_at")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChatMessage[];
}

export async function sendMessage(
  conversationId: string,
  senderId: string,
  body: string,
): Promise<ChatMessage> {
  const { data, error } = await supabase
    .from("messages")
    .insert({ conversation_id: conversationId, sender_id: senderId, body })
    .select()
    .single();
  if (error) throw error;
  return data as ChatMessage;
}

/** Marca como leídos los mensajes que no son del usuario actual. */
export async function markConversationRead(
  conversationId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from("messages")
    .update({ read_at: new Date().toISOString() })
    .eq("conversation_id", conversationId)
    .neq("sender_id", userId)
    .is("read_at", null);
  if (error) throw error;
}